import { Rule, RuleObject } from "antd/lib/form";
import debounce from "debounce-promise";
import { BaseResponsePaginationProps, BaseResponseProps } from "../types/config.type";
import { OfficeProperties } from "../types/office.type";
import { ProfileProperties } from "../types/profile.type";
import { httpRequest } from "./api";
import { ValidateErrorEntity } from "rc-field-form/lib/interface";
import { EmployeeProperties } from "../types/employee.type";
import { FormInstance } from "antd/es/form/Form";
import { getErrorMessage } from "./errorHandler";

type RuleType =
	| "required"
	| "email"
	| "phoneNumber"
	| "letter-and-space"
	| "password"
	| "positiveNumeric"
	| "decimal"
	| "nik"
	| "subdomain"
	| "youtube"
	| "required-start-date"
	| "min-15-characters"
	| "min-20-characters"
	| "price"
	| "max-160-characters"
	| "max-60-characters"
	| "min-max-phoneNumber"
	| "min-max-homePhone"
	| "npwp"
	| "bank-account-number";

export function generateFormRules(formName: string, rules: Array<RuleType> = []): Rule[] {
	const formRules: Rule[] = [];
	if (rules.includes("required")) {
		formRules.push({
			required: true,
			message: `${formName} is required.`,
		});
	}
	if (rules.includes("email")) {
		formRules.push({
			pattern:
				/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
			message: `${formName} format is invalid.`,
		});
	}
	if (rules.includes("phoneNumber")) {
		formRules.push({
			pattern: /^[0-9+ -]+$/,
			message: `${formName} can only include numbers, dash, plus and spacing.`,
		});
	}
	if (rules.includes("letter-and-space")) {
		formRules.push({
			pattern: /^[a-zA-Z ']+$/,
			message: `${formName} can only include letters.`,
		});
	}
	if (rules.includes("password")) {
		formRules.push({
			pattern: /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?.,+-=/;:"<>&])[A-Za-z\d@$!%*#?.,+-=/;:"<>&]{8,}$/,
			message: `${formName} minimum 8 characters (letter, number and special character)`,
		});
	}

	if (rules.includes("positiveNumeric")) {
		formRules.push({
			pattern: /^[0-9]+$/,
			message: `${formName} can only include positive numbers.`,
		});
	}

	if (rules.includes("decimal")) {
		formRules.push({
			pattern: /^[0-9.]+$/,
			message: `${formName} can only include numbers and dot (.).`,
		});
	}

	if (rules.includes("nik")) {
		formRules.push({
			pattern: /^[0-9]{16}$/,
			message: `${formName} must be 16 numbers.`,
		});
	}

	if (rules.includes("subdomain")) {
		formRules.push({
			pattern: /^[0-9a-zA-Z.-]+$/,
			message: `${formName} only contain alphabet, numeric, dot (.) and dash.`,
		});
		formRules.push();
	}

	if (rules.includes("youtube")) {
		formRules.push({
			pattern:
				/^((?:https?:)?\/\/)?((?:www|m)\.)?((?:youtube(-nocookie)?\.com|youtu.be))(\/(?:[\w-]+\?v=|embed\/|v\/)?)([\w-]+)(\S+)?$/,
			message: `${formName} is not valid youtube url!`,
		});
	}

	if (rules.includes("min-15-characters")) {
		formRules.push({
			pattern: /^.{15,}$/,
			message: `${formName} must be at least 15 characters.`,
		});
	}

	if (rules.includes("min-20-characters")) {
		formRules.push({
			validator: validateMin20Characters,
		});
	}

	if (rules.includes("max-160-characters")) {
		formRules.push({
			pattern: /^.{0,160}$/,
			message: `${formName} can't be more than 160 characters.`,
		});
	}

	if (rules.includes("max-60-characters")) {
		formRules.push({
			validator: async (_: RuleObject, value: any) => {
				if (value && value.replace(/<[^>]*>/g, "").length <= 60) {
					return Promise.resolve();
				} else {
					return Promise.reject(`${formName} can't be more than 60 characters.`);
				}
			},
		});
	}

	if (rules.includes("min-max-phoneNumber")) {
		formRules.push({
			validator: async (_: RuleObject, value: any) => {
				if (value) {
					if (value.replace(/<[^>]*>/g, "").length >= 10 && value.replace(/<[^>]*>/g, "").length <= 13) {
						return Promise.resolve();
					} else {
						return Promise.reject(`${formName} must be 10-13 numbers.`);
					}
				}
			},
		});
	}

	if (rules.includes("min-max-homePhone")) {
		formRules.push({
			validator: async (_: RuleObject, value: any) => {
				if (value) {
					if (value.replace(/<[^>]*>/g, "").length >= 7 && value.replace(/<[^>]*>/g, "").length <= 10) {
						return Promise.resolve();
					} else {
						return Promise.reject(`${formName} must be 7-10 numbers.`);
					}
				}
			},
		});
	}

	if (rules.includes("npwp")) {
		formRules.push({
			validator: async (_: RuleObject, value: any) => {
				if (value) {
					if (value.replace(/<[^>]*>/g, "").length >= 15 && value.replace(/<[^>]*>/g, "").length <= 16) {
						return Promise.resolve();
					} else {
						return Promise.reject(`${formName} must be 15-16 numbers.`);
					}
				}
			},
		});
	}

	if (rules.includes("bank-account-number")) {
		formRules.push({
			validator: async (_: RuleObject, value: any) => {
				if (value) {
					if (value.replace(/<[^>]*>/g, "").length >= 10 && value.replace(/<[^>]*>/g, "").length <= 16) {
						return Promise.resolve();
					} else {
						return Promise.reject(`${formName} must be 10-16 numbers.`);
					}
				}
			},
		});
	}

	if (rules.includes("price")) {
		formRules.push({
			validator: validatePrice,
		});
	}

	if (rules.includes("required-start-date")) {
		formRules.push({
			validator: async (_: RuleObject, value: any) => {
				if (!value?.[0]) {
					throw new Error(`${formName} must not be empty!`);
				}
			},
		});
	}

	return formRules;
}

const validateMin20Characters = (_: any, value: string) => {
	if (value && value.replace(/<[^>]*>/g, "").length >= 20) {
		return Promise.resolve();
	}
	return Promise.reject("Description must be at least 20 characters.");
};

const validatePrice = (_: any, value: number) => {
	if (value && +value >= 0) {
		return Promise.resolve();
	}
	return Promise.reject("Price must be greater than 0");
};

const checkSubdomain = debounce(async (value: string, excludeOfficeId?: string) => {
	try {
		let url = "offices?filterSubdomain=" + value?.toLowerCase() + "&start=0&limit=1";
		const { data } = await httpRequest.get<BaseResponsePaginationProps<OfficeProperties>>(url);
		if (data.payload.count > 0) {
			let row = data.payload.results[0];

			if (row.officeId === excludeOfficeId) {
				return Promise.resolve("Subdomain available!");
			} else {
				return Promise.reject("Subdomain is alredy used");
			}
		} else {
			return Promise.resolve("Subdomain available!");
		}
	} catch (err) {
		return Promise.resolve("Subdomain available!");
	}
}, 500);

export const validateSubdomain = (value: string, excludeOfficeId?: string) => {
	return new Promise((resolve, reject) => {
		if (value === undefined || value.trim().length < 1) {
			return resolve("");
		}
		checkSubdomain(value, excludeOfficeId)
			.then((res) => {
				resolve(res);
			})
			.catch((err) => {
				reject(new Error(err));
			});
	});
};

const checkUsernameAgent = debounce(async (value: string, employeeId?: string) => {
	try {
		let url = "employees?filterSlug=" + value?.toLowerCase() + "&start=0&limit=1";
		let { data } = await httpRequest.get<BaseResponsePaginationProps<EmployeeProperties>>(url);
		if (data.payload.count > 0) {
			let row = data.payload.results[0];
			if (row.employeeId === employeeId) {
				return Promise.resolve("Website link can be used");
			} else {
				return Promise.reject("Website link is already used");
			}
		} else {
			return Promise.resolve("Website link can be used");
		}
	} catch (err) {
		return Promise.resolve("Website link can be used");
	}
}, 500);

export const validateUsernameAgent = (value: string, employeeId?: string) => {
	return new Promise((resolve, reject) => {
		if (value === undefined || value.trim().length < 1) {
			return resolve("");
		}
		checkUsernameAgent(value, employeeId)
			.then((res) => {
				resolve(res);
			})
			.catch((err) => {
				reject(new Error(err));
			});
	});
};

export const validateSlugAuthor = (value: string, employeeId?: string) => {
	return new Promise((resolve, reject) => {
		if (value === undefined || value.trim().length < 1) {
			return resolve("");
		}
		checkSlugAuthor(value, employeeId)
			.then((res) => {
				resolve(res);
			})
			.catch((err) => {
				reject(new Error(err));
			});
	});
};

const checkSlugAuthor = debounce(async (value: string, employeeId?: string) => {
	try {
		let url = "employees?filterSlugAuthor=" + value?.toLowerCase() + "&start=0&limit=1";
		let { data } = await httpRequest.get<BaseResponsePaginationProps<EmployeeProperties>>(url);
		if (data.payload.count > 0) {
			let row = data.payload.results[0];
			if (row.employeeId === employeeId) {
				return Promise.resolve("Website link can be used");
			} else {
				return Promise.reject("Website link is already used");
			}
		} else {
			return Promise.resolve("Website link can be used");
		}
	} catch (err) {
		return Promise.resolve("Website link can be used");
	}
}, 500);

const checkEmail = debounce(async (value: string, excludeUserId?: string) => {
	const { data } = await httpRequest.post<BaseResponseProps<ProfileProperties | null>>("users/check-email", {
		email: value?.toLowerCase(),
	});
	if (data.payload !== null) {
		let row = data.payload;

		if (row.userId === excludeUserId) {
			return Promise.resolve("Email available!");
		} else {
			return Promise.reject("Email is already used");
		}
	} else {
		return Promise.resolve("Email available!");
	}
}, 500);

export const validateEmail = (value: string, excludeUserId?: string) => {
	return new Promise((resolve, reject) => {
		if (value === undefined || value === null || value?.trim().length < 1) {
			return resolve("");
		}
		checkEmail(value, excludeUserId)
			.then((res) => {
				resolve(res);
			})
			.catch((err) => {
				reject(new Error(err));
			});
	});
};

export const handleSubmitFailed = <Values = any>({ errorFields }: ValidateErrorEntity<Values>) => {
	const firstErrorField = errorFields[0];
	let label = firstErrorField.name.join("_");

	const firstErrorFieldNode = document.querySelector(`label[for="${label}"]`);
	if (firstErrorFieldNode) {
		firstErrorFieldNode.scrollIntoView({ behavior: "smooth", block: "center" });
	}
};

export const resetErrorFormItem = (form: FormInstance, name: string, value: any) => {
	form.setFields([
		{
			name,
			value,
			errors: [],
		},
	]);
};

export const validateEmailPortal = (
	body: { value: string; portalId: string; officeId?: string; employeeId?: string },
	type: "office" | "agent"
) => {
	const { value, portalId } = body;
	return new Promise((resolve, reject) => {
		if (value === undefined || value === null || value?.trim().length < 1) {
			return resolve("");
		}

		if (portalId === undefined || portalId === null || portalId?.trim().length < 1) {
			return resolve("");
		}

		checkEmailPortal(body, type)
			.then((res) => {
				resolve(res);
			})
			.catch((err) => {
				reject(new Error(err));
			});
	});
};

const checkEmailPortal = debounce(
	async (
		body: {
			value: string;
			portalId: string;
			officeId?: string;
			employeeId?: string;
		},
		type: "office" | "agent"
	) => {
		try {
			const endpoint =
				type === "office"
					? "office/office-portal-profiles/validate-email"
					: "office/agent-portal-profiles/validate-email";
			const { data } = await httpRequest.post<BaseResponseProps<{ isPassed: boolean; message: string }>>(
				endpoint,
				{
					email: body.value,
					portalId: body.portalId,
					officeId: body.officeId,
					employeeId: body.employeeId,
				}
			);
			let row = data.payload;

			if (row.isPassed) {
				return Promise.resolve(data.payload.message);
			} else {
				return Promise.reject(data.payload.message);
			}
		} catch (error) {
			return Promise.reject(getErrorMessage(error));
		}
	},
	500
);
